import { ChevronDown, ChevronRight, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import { CatalogFilterInfoTooltip } from '~/components/common/catalog-filter-info-banner';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Input } from '~/components/ui/input';
import { Switch } from '~/components/ui/switch';
import type {
  CurationFilterDefinition,
  ProductFilterValues,
} from '~/features/catalog-operations/types/catalog-operations.types';
import { cn } from '~/utils/cn';

/** Options shown before the rest collapse behind "Show n more". */
const VISIBLE_OPTIONS = 6;
/** At or above this many options a facet gets its own search box — scrolling a
 *  16-item checkbox list to find one value is what makes filtering feel heavy. */
const SEARCHABLE_AT = 8;

interface Props {
  /** Every filter the catalog exposes, in the order the API returns them. All
   *  four types render here — the rail mirrors the drawer rather than handling a
   *  subset, so there is one place to change when the API adds a filter. */
  definitions: CurationFilterDefinition[];
  applied: ProductFilterValues;
  onChange: (filters: ProductFilterValues) => void;
  /** Facet keys that start collapsed — secondary ones, so the rail stays short. */
  collapsedByDefault?: string[];
  disabled?: boolean;
  className?: string | undefined;
  /** E2E `data-testid` root; children derive `${testId}-...`. */
  testId?: string;
}

/** Rebuilds the filter map without the given keys. `delete` on a computed key is
 *  banned by lint, and rebuilding avoids mutating the caller's object anyway. */
function omitKeys(filters: ProductFilterValues, keys: string[]): ProductFilterValues {
  return Object.fromEntries(Object.entries(filters).filter(([key]) => !keys.includes(key)));
}

/** Current selection for a multi-select facet, always as an array. */
function selectionOf(applied: ProductFilterValues, key: string): string[] {
  const value = applied[key];
  return Array.isArray(value) ? value : [];
}

/** Current value for a text filter. */
function textOf(applied: ProductFilterValues, key: string): string {
  const value = applied[key];
  return typeof value === 'string' ? value : '';
}

/** Current value for a boolean filter. */
function boolOf(applied: ProductFilterValues, key: string): boolean {
  return applied[key] === true;
}

/** How many active selections a definition contributes, for its header badge. */
function activeCount(applied: ProductFilterValues, def: CurationFilterDefinition): number {
  if (def.type === 'multi_select') return selectionOf(applied, def.key).length;
  if (def.type === 'boolean') return boolOf(applied, def.key) ? 1 : 0;
  return textOf(applied, def.key).trim() === '' ? 0 : 1;
}

/**
 * Always-visible facet rail. Replaces the round trip of opening a drawer,
 * changing one value and closing it again — and because it stays open, the
 * current selection is legible while the user is looking at the results.
 * Only multi-select facets appear here.
 */
export function ProductFilterRail({
  definitions,
  applied,
  onChange,
  collapsedByDefault = [],
  disabled = false,
  className,
  testId = 'products-filter-rail',
}: Props) {
  // A multi_select with no options means the catalog has no values for it —
  // the banner explains that, and an empty checkbox list would just be noise.
  const facets = useMemo(
    () => definitions.filter((d) => d.type !== 'multi_select' || d.options.length > 0),
    [definitions],
  );
  const [collapsed, setCollapsed] = useState(new Set(collapsedByDefault));
  const [expanded, setExpanded] = useState(new Set<string>());
  const [queries, setQueries] = useState<Record<string, string>>({});

  if (facets.length === 0) return null;

  const totalApplied = facets.reduce((n, f) => n + activeCount(applied, f), 0);

  /** Adds or removes one value, dropping the key entirely when it empties so the
   *  request doesn't carry `key=[]`. */
  function toggle(key: string, option: string) {
    const current = selectionOf(applied, key);
    const next = current.includes(option)
      ? current.filter((v) => v !== option)
      : [...current, option];
    onChange(next.length === 0 ? omitKeys(applied, [key]) : { ...applied, [key]: next });
  }

  function clearFacets() {
    // Only the facets this rail owns — a free-text filter set in the drawer
    // shouldn't disappear because someone cleared the rail.
    onChange(
      omitKeys(
        applied,
        facets.map((f) => f.key),
      ),
    );
  }

  /** Commits a text filter. Blank removes the key so the request doesn't carry an
   *  empty param. Fired on Enter and on blur rather than per keystroke — a
   *  request per character would hammer the listing endpoint. */
  function commitText(key: string, raw: string) {
    const value = raw.trim();
    onChange(value === '' ? omitKeys(applied, [key]) : { ...applied, [key]: value });
  }

  function setBoolean(key: string, next: boolean) {
    onChange(next ? { ...applied, [key]: true } : omitKeys(applied, [key]));
  }

  function flip(set: Set<string>, key: string): Set<string> {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  }

  return (
    <aside
      data-testid={testId}
      aria-label="Product filters"
      className={cn('flex flex-col gap-0 overflow-y-auto border-r', className)}
    >
      <div className="flex items-center justify-between gap-2 px-4 pt-4 pb-2">
        <span className="text-muted-foreground flex items-center gap-1.5 text-[11px] font-bold tracking-wider uppercase">
          Filters
          {/* Icon, not the banner: at 13.5rem the banner wraps to seven lines and
              costs more vertical space than the facets it explains. */}
          <CatalogFilterInfoTooltip />
        </span>
        <Button
          data-testid={`${testId}-clear-all-button`}
          type="button"
          variant="link"
          disabled={disabled || totalApplied === 0}
          onClick={clearFacets}
          className={cn(
            'h-auto p-0 text-xs font-semibold',
            disabled || totalApplied === 0 ? 'cursor-not-allowed' : 'cursor-pointer',
          )}
        >
          Clear all
        </Button>
      </div>

      {facets.map((facet) => {
        const selected = selectionOf(applied, facet.key);
        const isShut = collapsed.has(facet.key);
        const query = (queries[facet.key] ?? '').trim().toLowerCase();
        const matching = query
          ? facet.options.filter((o) => o.toLowerCase().includes(query))
          : facet.options;
        // A search narrows the list, so the "show more" cap stops applying.
        const showAll = expanded.has(facet.key) || query !== '';
        const shown = showAll ? matching : matching.slice(0, VISIBLE_OPTIONS);
        const hiddenCount = matching.length - shown.length;

        return (
          <div
            key={facet.key}
            data-testid={`${testId}-section-${facet.key}`}
            className="border-t px-4 py-3"
          >
            <Button
              data-testid={`${testId}-${facet.key}-toggle-button`}
              type="button"
              variant="ghost"
              onClick={() => {
                setCollapsed((s) => flip(s, facet.key));
              }}
              aria-expanded={!isShut}
              className="mb-1 flex h-auto w-full cursor-pointer items-center justify-between gap-2 p-0 text-left hover:bg-transparent"
            >
              <span className="text-sm font-semibold">{facet.label}</span>
              <span className="flex items-center gap-2">
                {activeCount(applied, facet) > 0 && (
                  <span
                    data-testid={`${testId}-${facet.key}-count-badge`}
                    className="bg-selected-row text-primary rounded-full px-1.5 text-[10px] font-bold"
                  >
                    {activeCount(applied, facet)}
                  </span>
                )}
                {isShut ? (
                  <ChevronRight className="text-muted-foreground size-3.5" />
                ) : (
                  <ChevronDown className="text-muted-foreground size-3.5" />
                )}
              </span>
            </Button>

            {!isShut && facet.type === 'boolean' && (
              <div className="flex items-center justify-between gap-2 pt-1 text-xs">
                <label
                  htmlFor={`filter-${facet.key}`}
                  className={cn(
                    'text-muted-foreground',
                    disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                  )}
                >
                  Only show matching
                </label>
                <Switch
                  data-testid={`${testId}-${facet.key}-toggle`}
                  id={`filter-${facet.key}`}
                  checked={boolOf(applied, facet.key)}
                  disabled={disabled}
                  onCheckedChange={(next) => {
                    setBoolean(facet.key, next);
                  }}
                  className="cursor-pointer"
                />
              </div>
            )}

            {!isShut && (facet.type === 'text' || facet.type === 'text_contains') && (
              <Input
                data-testid={`${testId}-${facet.key}-input`}
                type="text"
                defaultValue={textOf(applied, facet.key)}
                disabled={disabled}
                placeholder={
                  facet.type === 'text_contains'
                    ? `Search ${facet.label.toLowerCase()}`
                    : `Enter ${facet.label.toLowerCase()}`
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitText(facet.key, e.currentTarget.value);
                }}
                onBlur={(e) => {
                  commitText(facet.key, e.currentTarget.value);
                }}
                className="mt-1 h-8 text-xs"
              />
            )}

            {!isShut && facet.type === 'multi_select' && (
              <div className="flex flex-col gap-1.5 pt-1">
                {facet.options.length >= SEARCHABLE_AT && (
                  <Input
                    data-testid={`${testId}-${facet.key}-search-input`}
                    type="text"
                    value={queries[facet.key] ?? ''}
                    disabled={disabled}
                    placeholder={`Search ${facet.label.toLowerCase()}`}
                    onChange={(e) => {
                      setQueries((q) => ({ ...q, [facet.key]: e.target.value }));
                    }}
                    className="h-7 text-xs"
                  />
                )}

                {shown.length === 0 ? (
                  <p
                    data-testid={`${testId}-${facet.key}-no-matches`}
                    className="text-muted-foreground text-xs"
                  >
                    No matches
                  </p>
                ) : (
                  <div
                    className={cn(
                      'flex flex-col gap-1.5',
                      // Only the expanded list needs its own scroll; the collapsed
                      // six fit without one.
                      shown.length > VISIBLE_OPTIONS && 'max-h-56 overflow-y-auto pr-1',
                    )}
                  >
                    {shown.map((option) => (
                      <label
                        key={option}
                        className={cn(
                          'flex items-center gap-2 text-xs',
                          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                        )}
                      >
                        <Checkbox
                          data-testid={`${testId}-option-${facet.key}-${option.replace(/\s+/g, '-').toLowerCase()}-checkbox`}
                          checked={selected.includes(option)}
                          disabled={disabled}
                          onCheckedChange={() => {
                            toggle(facet.key, option);
                          }}
                          className="cursor-pointer"
                        />
                        <span
                          className={cn(
                            'min-w-0 flex-1 truncate',
                            selected.includes(option) && 'font-semibold',
                          )}
                          title={option}
                        >
                          {option}
                        </span>
                      </label>
                    ))}
                  </div>
                )}

                {hiddenCount > 0 && (
                  <Button
                    data-testid={`${testId}-${facet.key}-show-more-button`}
                    type="button"
                    variant="link"
                    onClick={() => {
                      setExpanded((s) => new Set(s).add(facet.key));
                    }}
                    className="h-auto cursor-pointer justify-start p-0 text-xs font-semibold"
                  >
                    Show {hiddenCount} more
                  </Button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </aside>
  );
}

/**
 * Removable chips for every applied facet value, shown above the results. This is
 * what makes the rail worth having over a drawer: the active selection stays
 * legible while the user is reading the products it produced.
 */
export function AppliedFilterChips({
  definitions,
  applied,
  onChange,
  className,
  testId = 'products',
}: {
  definitions: CurationFilterDefinition[];
  applied: ProductFilterValues;
  onChange: (filters: ProductFilterValues) => void;
  className?: string | undefined;
  /** E2E `data-testid` prefix; root is `${testId}-applied-filters`, chips `${testId}-applied-filter-chip-<key>-<value>`. */
  testId?: string;
}) {
  const chips = definitions.flatMap((def) =>
    selectionOf(applied, def.key).map((value) => ({ key: def.key, label: def.label, value })),
  );
  if (chips.length === 0) return null;

  function remove(key: string, value: string) {
    const next = selectionOf(applied, key).filter((v) => v !== value);
    onChange(next.length === 0 ? omitKeys(applied, [key]) : { ...applied, [key]: next });
  }

  function clearAll() {
    onChange(
      omitKeys(
        applied,
        chips.map((chip) => chip.key),
      ),
    );
  }

  return (
    <div
      data-testid={`${testId}-applied-filters`}
      className={cn('flex flex-wrap items-center gap-1.5', className)}
    >
      <span
        data-testid={`${testId}-applied-filters-count`}
        className="text-muted-foreground text-[11px] font-bold tracking-wider uppercase"
      >
        {chips.length} filter{chips.length === 1 ? '' : 's'}
      </span>
      {chips.map((chip) => (
        <span
          key={`${chip.key}-${chip.value}`}
          data-testid={`${testId}-applied-filter-chip-${chip.key}-${chip.value.replace(/\s+/g, '-').toLowerCase()}`}
          className="flex items-center gap-1.5 rounded-full border bg-white py-0.5 pr-1 pl-2.5 text-xs font-semibold"
        >
          <span className="text-muted-foreground font-medium">{chip.label}:</span>
          {chip.value}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            data-testid={`${testId}-applied-filter-chip-${chip.key}-${chip.value.replace(/\s+/g, '-').toLowerCase()}-remove-button`}
            aria-label={`Remove ${chip.label} ${chip.value}`}
            onClick={() => {
              remove(chip.key, chip.value);
            }}
            className="bg-muted size-4 cursor-pointer rounded-full p-0"
          >
            <X className="size-2.5" />
          </Button>
        </span>
      ))}
      <Button
        data-testid={`${testId}-applied-filters-clear-all-button`}
        type="button"
        variant="link"
        onClick={clearAll}
        className="h-auto cursor-pointer p-0 text-xs font-semibold"
      >
        Clear all
      </Button>
    </div>
  );
}
